import React, { useState } from "react";
import { useRouter } from "next/router";
import { successToast, errorToast } from "../../utils/toast";
import { reportAssetCall } from "../../services/campaign";
import { useAuth } from "../../contexts/AuthContext";

const REPORT_REASONS = [
  { value: "INAPPROPRIATE", label: "Inappropriate content" },
  { value: "COPYRIGHT", label: "Copyright or IP violation" },
  { value: "SPAM", label: "Spam or misleading" },
  { value: "SCAM", label: "Scam or fraud" },
  { value: "OTHER", label: "Something else" },
];

const MIN_DESCRIPTION = 20;

const ReportAssetModal = ({ assetId, assetName, onClose }) => {
  const router = useRouter();
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const descriptionTooShort = description.trim().length < MIN_DESCRIPTION;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      errorToast("Please select a reason");
      return;
    }
    if (descriptionTooShort) {
      errorToast(`Please describe the issue (at least ${MIN_DESCRIPTION} characters)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await reportAssetCall(assetId, {
        reason,
        description: description.trim(),
      });
      // publicApiCall.post fires its own errorToast on failure; only the
      // success path needs a toast here.
      successToast(res?.message || "Report submitted. Our team will review it shortly.");
      onClose();
    } catch (error) {
      // errorToast already shown by the API layer — just log.
      console.error("Report submit error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-jacarta-700 p-5 sm:p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Report this item
            </h3>
            {assetName && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-jacarta-300 break-words">
                {assetName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-4 text-gray-400 hover:text-gray-600 dark:text-jacarta-300 dark:hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!user ? (
          <div>
            <p className="text-sm text-gray-600 dark:text-jacarta-300 mb-5">
              You need an account to report content - it keeps reports accountable.
              Sign in or create a free account to continue. You can keep browsing either way.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => router.push("/sign-in")}
                className="flex-1 px-4 py-2 rounded-lg bg-accent hover:bg-accent-dark text-sm font-semibold text-white"
              >
                Sign In
              </button>
              <button
                onClick={() => router.push("/sign-up")}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-200 dark:bg-jacarta-600 text-sm font-semibold text-gray-700 dark:text-white"
              >
                Create Account
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-jacarta-300 mb-2">
                Why are you reporting this?
              </label>
              <div className="space-y-2">
                {REPORT_REASONS.map((r) => (
                  <label
                    key={r.value}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 cursor-pointer transition-colors ${
                      reason === r.value
                        ? "border-accent bg-accent/5 dark:bg-accent/10"
                        : "border-gray-200 dark:border-jacarta-600 hover:border-gray-300 dark:hover:border-jacarta-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-accent"
                    />
                    <span className="text-sm text-gray-800 dark:text-white">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-jacarta-300 mb-1">
                Tell us more
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe the issue so our team can review it…"
                className="w-full bg-white dark:bg-jacarta-800 border border-gray-300 dark:border-jacarta-600 rounded-lg px-4 py-2 text-sm text-gray-700 dark:text-white focus:outline-none focus:border-accent"
              />
              <p
                className={`mt-1 text-xs ${
                  descriptionTooShort ? "text-gray-400 dark:text-jacarta-400" : "text-green-600"
                }`}
              >
                {descriptionTooShort
                  ? `At least ${MIN_DESCRIPTION} characters (${description.trim().length}/${MIN_DESCRIPTION})`
                  : "Looks good"}
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-jacarta-600 text-sm font-semibold text-gray-700 dark:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !reason || descriptionTooShort}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting…" : "Submit Report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportAssetModal;
